import { useMemo, useState } from "react";
import { useTenantOrganization, useTenantRoles } from "../../../lib/api";
import { PERMISSIONS, levelOf, policyLabel } from "../../../lib/access";
import { PERMISSION_GROUPS, emptyRole, knownPermissions } from "../../../lib/organization";
import { useRoleCellSave } from "../../SuperAdmin/useRoleForm";
import { Icon } from "../../../components/msd/Icon";
import { AN_T } from "../../../components/msd/analytics";
import { Btn, CARD, Page, PageHead, cx, initialsOf } from "../../../components/msd/ui";
import RoleSheet from "./RoleSheet";
import { OrgLoading, plural } from "./kit";
import { PERMISSION_ICON, POLICY_ICON } from "./roleIcons";

const BADGE =
  "inline-flex shrink-0 items-center justify-center bg-msd-soft font-semibold tracking-[.02em] text-msd-ink2 shadow-[inset_0_0_0_1px_var(--line)]";

// .rl-badge — the role's short name on a quiet grey tile. The prototype's codes are two to
// four letters; a brand can set a longer one, so the badge grows past its square
// and the text steps down rather than being clipped.
function Badge({ role, small = false }) {
  const given = role.short_label || initialsOf(role.label);
  // Past five letters it is a word, not a code, so abbreviate it as the prototype does.
  const text = given.length > 5 ? given.slice(0, 3).toUpperCase() : given;
  const long = text.length > 4;
  return (
    <span
      className={cx(
        BADGE,
        "px-[6px]",
        small ? "h-7 min-w-[38px] rounded-lg" : "h-[46px] min-w-[46px] rounded-[13px]",
        small ? (long ? "text-[9.5px]" : "text-[11px]") : long ? "text-[10.5px]" : "text-[13px]",
      )}
      title={role.label}
    >
      {text}
    </span>
  );
}

/** Who holds each role, for the avatar stacks — only read for a login that
 *  may see People, so this page names nobody that page would not. */
function WithHolders({ children }) {
  const { data } = useTenantOrganization(null);
  const byRole = useMemo(() => {
    const m = new Map();
    for (const p of data?.people || []) {
      if (!m.has(p.role)) m.set(p.role, []);
      m.get(p.role).push(p);
    }
    return m;
  }, [data]);
  return children(byRole);
}

// .av-stack
function Holders({ role, people }) {
  const total = role.user_count || 0;
  if (!total) return <span className="text-[13px] text-msd-muted">No one yet</span>;
  if (!people) return <span className="text-[13px] text-msd-muted">{plural(total, "person", "people")}</span>;
  const shown = people.slice(0, 4);
  const more = Math.max(total, people.length) - shown.length;
  const AV =
    "-ml-[6px] flex h-[30px] w-[30px] items-center justify-center rounded-full bg-msd-soft text-[11px] font-bold text-msd-ink2 shadow-[0_0_0_2px_var(--surface)] first:ml-0";
  return (
    <span className="flex items-center" aria-label={plural(total, "person", "people")}>
      {shown.map((p) => (
        <span key={p.id} className={AV} title={p.name || p.email}>
          {initialsOf(p.name || p.email)}
        </span>
      ))}
      {more > 0 && <span className={AV}>+{more}</span>}
    </span>
  );
}

/**
 * Roles and access: every role the brand has, what each one sees and which
 * features it opens, and a matrix to compare (and change) them side by side.
 * Same data and writes as Super Admin's Roles editor.
 */
export default function RolesPage({ canEdit, canSeePeople }) {
  if (canSeePeople) return <WithHolders>{(byRole) => <Roles canEdit={canEdit} byRole={byRole} />}</WithHolders>;
  return <Roles canEdit={canEdit} byRole={null} />;
}

function Roles({ canEdit, byRole }) {
  const { data, isLoading } = useTenantRoles(null);
  const [editing, setEditing] = useState(null);
  const cell = useRoleCellSave(null);
  const roles = data?.rows || [];

  if (isLoading) return <OrgLoading />;

  const people = roles.reduce((n, r) => n + (r.user_count || 0), 0);

  const card = (r) => {
    const inner = (
      <>
        <div className="flex items-center gap-3">
          <Badge role={r} />
          <div className="min-w-0 grow">
            <div className="truncate text-[16.5px] font-bold tracking-[-0.01em]">{r.label}</div>
            <div className="flex items-center gap-[5px] text-[13px] text-msd-muted">
              <Icon name={POLICY_ICON[r.access_policy] || "map"} size={13} stroke={2} />
              {policyLabel(r.access_policy)}
              <span className="text-msd-faint">· {r.code}</span>
            </div>
          </div>
          {r.is_system && (
            <span className="flex text-msd-muted" title="Built in" aria-label="Built-in role">
              <Icon name="lock" size={14} stroke={2} />
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-[10px]">
          <Holders role={r} people={byRole ? byRole.get(r.code) || [] : null} />
          <span className="text-[13px] text-msd-muted">
            {knownPermissions(r).length} of {PERMISSIONS.length} features
          </span>
        </div>
      </>
    );
    const cls = cx(CARD, "flex flex-col gap-[14px] px-[26px] py-6 text-left text-inherit");
    return canEdit ? (
      <button
        key={r.id}
        type="button"
        onClick={() => setEditing(r)}
        aria-label={`Edit ${r.label}`}
        className={cx(
          cls,
          "cursor-pointer border-0 hover:shadow-[0_0_0_.5px_rgba(16,24,40,.06),0_6px_14px_rgba(16,24,40,.08),0_18px_36px_rgba(16,24,40,.08)]",
        )}
      >
        {inner}
      </button>
    ) : (
      <div key={r.id} className={cls}>
        {inner}
      </div>
    );
  };

  const matrixCell = (r, p) => {
    const level = p.levelled ? levelOf(r.permissions, p.id) : (r.permissions || []).includes(p.id);
    const word = p.levelled ? { read: "Read", edit: "Edit" }[level] || "None" : level ? "on" : "off";
    const box = cx(
      "inline-flex h-[34px] w-[34px] items-center justify-center rounded-[10px] border-0 text-white",
      level ? "bg-msd-btn" : "bg-transparent shadow-[inset_0_0_0_1.5px_var(--line)]",
    );
    const mark = level ? (
      p.levelled ? (
        <span className="text-[11px] font-bold">{word}</span>
      ) : (
        <Icon name="check" size={15} stroke={2.8} />
      )
    ) : null;
    return (
      <td key={r.id} className="px-[6px] py-2 text-center">
        {canEdit ? (
          <button
            type="button"
            onClick={() => cell.tap(r, p)}
            disabled={cell.pending}
            aria-label={`${r.label}: ${p.label} ${word}`}
            title={`${r.label} · ${p.label}`}
            className={cx(
              box,
              "cursor-pointer disabled:cursor-wait",
              level ? "hover:brightness-[1.08]" : "hover:bg-msd-soft hover:shadow-[inset_0_0_0_1.5px_var(--line2)]",
            )}
          >
            {mark}
          </button>
        ) : (
          <span className={box} aria-label={`${r.label}: ${p.label} ${word}`}>
            {mark}
          </span>
        )}
      </td>
    );
  };

  return (
    <Page>
      <PageHead
        title="Roles and access"
        sub={`${plural(roles.length, "role", "roles")} · ${plural(people, "person", "people")}`}
      >
        {canEdit && (
          <Btn variant="pri" icon="plus2" onClick={() => setEditing(emptyRole())}>
            New role
          </Btn>
        )}
      </PageHead>

      <div className="grid grid-cols-[repeat(auto-fill,minmax(330px,1fr))] gap-4 max-[640px]:grid-cols-1">
        {roles.map(card)}
      </div>

      {roles.length > 0 && (
        <section className={cx(CARD, "px-[6px] pb-[10px] pt-[18px]")}>
          <div className="flex flex-wrap items-baseline justify-between gap-[10px] px-[18px] pb-2">
            <div className={AN_T}>Compare access</div>
            {canEdit && (
              <span className="text-[13px] text-msd-muted">Tap a cell to change it. Levelled features go None, Read, Edit.</span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="sticky top-0 bg-msd-surface" />
                  {roles.map((r) => (
                    <th key={r.id} className="sticky top-0 bg-msd-surface px-[6px] py-2 text-center font-medium">
                      <Badge role={r} small />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_GROUPS.map(({ label, items }) => [
                  <tr key={`g-${label}`}>
                    <td
                      colSpan={roles.length + 1}
                      className="px-[18px] pb-[6px] pt-[14px] text-left text-[12px] font-semibold uppercase tracking-[.04em] text-msd-muted"
                    >
                      {label || "Features"}
                    </td>
                  </tr>,
                  ...items.map((p) => (
                    <tr key={p.id} className="hover:bg-msd-soft">
                      <th scope="row" className="whitespace-nowrap py-2 pl-[18px] pr-[6px] text-left text-[14.5px] font-medium text-msd-ink">
                        <span className="flex items-center gap-[10px]">
                          <Icon name={PERMISSION_ICON[p.id] || "shield"} size={16} stroke={2} />
                          {p.label}
                        </span>
                      </th>
                      {roles.map((r) => matrixCell(r, p))}
                    </tr>
                  )),
                ])}
                <tr>
                  <td
                    colSpan={roles.length + 1}
                    className="px-[18px] pb-[6px] pt-[14px] text-left text-[12px] font-semibold uppercase tracking-[.04em] text-msd-muted"
                  >
                    Can see
                  </td>
                </tr>
                <tr className="hover:bg-msd-soft">
                  <th scope="row" className="whitespace-nowrap py-2 pl-[18px] pr-[6px] text-left text-[14.5px] font-medium text-msd-ink">
                    <span className="flex items-center gap-[10px]">
                      <Icon name="eye" size={16} stroke={2} />
                      Scope
                    </span>
                  </th>
                  {roles.map((r) => (
                    <td key={r.id} className="px-[6px] py-2 text-center text-[12px] text-msd-muted">
                      {policyLabel(r.access_policy)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}

      {editing && <RoleSheet key={editing.id || "new"} role={editing} onClose={() => setEditing(null)} />}
    </Page>
  );
}
